import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useUpdateDiscordInfoMutation,
  useLazyGetDiscordGuildsQuery,
  useLazyGetDiscordChannelsQuery,
  useDisconnectDiscordMutation,
  useLazyGetDiscordConnectionStatusQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import SelectSkeleton from '../SelectSkeleton';
import DiscordIcon from '../../../assets/images/discord.svg';

interface Channel {
  id: string;
  name: string;
}

interface Guild {
  id: string;
  name: string;
}

const DiscordSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [updateDiscordInfo, { isLoading }] = useUpdateDiscordInfoMutation();
  const [getGuilds, { data: guildsData }] = useLazyGetDiscordGuildsQuery();
  const [getChannels, { data: channelsData, error: channelsError }] =
    useLazyGetDiscordChannelsQuery();
  const [disconnectDiscord] = useDisconnectDiscordMutation();
  const [getConnectionStatus, { data: connectionStatus }] =
    useLazyGetDiscordConnectionStatusQuery();
  const user = useCurrentUser();
  const [selectedGuild, setSelectedGuild] = useState<string>('');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [isLoadingGuilds, setIsLoadingGuilds] = useState(false);
  const [isChangingGuild, setIsChangingGuild] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);

  const params = new URLSearchParams(location.search);
  const companyId = params.get('companyId') || user?.companyId || '';

  useEffect(() => {
    if (companyId) {
      getConnectionStatus(companyId)
        .unwrap()
        .then((res: any) => {
          if (res?.guildId && !selectedGuild) {
            setSelectedGuild(res.guildId);
          }
        })
        .catch(() => {});
    }
  }, [companyId, getConnectionStatus]);

  useEffect(() => {
    if (companyId && !selectedGuild) {
      setIsLoadingGuilds(true);
      getGuilds(companyId)
        .unwrap()
        .then((res: any) => {
          if (res?.guilds?.length > 0 && !selectedGuild) {
            setSelectedGuild(res.guilds[0].id);
          }
        })
        .catch(() => {})
        .finally(() => setIsLoadingGuilds(false));
    }
  }, [companyId, selectedGuild, getGuilds]);

  useEffect(() => {
    if (selectedGuild && companyId) {
      setChannelError(null);
      getChannels({ companyId, guildId: selectedGuild });
    }
  }, [selectedGuild, companyId]);

  useEffect(() => {
    if (channelsError) {
      setChannelError(
        (channelsError as any)?.data?.message ||
          'Failed to load channels. The bot may not have access to this server.'
      );
    }
  }, [channelsError]);

  const handleSubmit = async () => {
    if (!selectedChannel || !selectedGuild || !companyId) {
      ReactToastify({
        success: false,
        message: 'Channel or guild missing',
      });
      return;
    }

    try {
      await updateDiscordInfo({
        companyId,
        data: {
          isDiscord: true,
          guildId: selectedGuild,
          channelId: selectedChannel,
        },
      }).unwrap();

      ReactToastify({
        success: true,
        message: 'Discord info updated successfully',
      });

      navigate('/dashboard/ask-tara');
    } catch (err) {
      console.error('Discord update failed:', err);
      ReactToastify({
        success: false,
        message: 'Something went wrong while saving Discord info',
      });
    }
  };

  const handleDisconnect = async () => {
    if (!companyId) return;
    try {
      await disconnectDiscord(companyId).unwrap();
      setSelectedGuild('');
      setSelectedChannel('');
      setIsChangingGuild(false);
      ReactToastify({ success: true, message: 'Discord disconnected' });
    } catch (err) {
      ReactToastify({
        success: false,
        message: 'Failed to disconnect Discord',
      });
    }
  };

  return (
    <div className="relative w-full h-screen  overflow-hidden font-inter">
      <div className="flex items-center justify-center flex-col h-full">
        <div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={DiscordIcon} alt="Discord" className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Discord</h1>
            </div>
          </div>

          {isLoadingGuilds || (!channelsData && !channelError) ? (
            <SelectSkeleton />
          ) : channelError ? (
            <p className="text-sm text-red-500">{channelError}</p>
          ) : (
            <>
              {isChangingGuild || !connectionStatus?.guildId ? (
                <div className="mb-4">
                  <label className="mb-2 text-xs font-roboto pl-2 block">
                    Select Server
                  </label>
                  <select
                    value={selectedGuild}
                    onChange={(e) => {
                      setSelectedGuild(e.target.value);
                      setSelectedChannel('');
                      setIsChangingGuild(false);
                    }}
                    className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                  >
                    {guildsData?.guilds?.map((guild: Guild) => (
                      <option key={guild.id} value={guild.id}>
                        {guild.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <p className="text-sm text-green-600 font-medium mb-4">
                  ✓ Using server: {connectionStatus.guildId}
                </p>
              )}

              <div>
                <label className="my-2 text-xs font-roboto pl-2 block">
                  Select Channel
                </label>
                <select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                >
                  <option value="">Select a Channel</option>
                  {channelsData?.channels?.map((ch: Channel) => (
                    <option key={ch.id} value={ch.id}>
                      {ch.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end items-center mt-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading || !selectedChannel}
                  className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center px-4 py-3 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiscordSelect;
